import React, { useState, useRef, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Alert, LinearProgress,
  Chip, Divider, IconButton, Tooltip, Grid, Card, CardContent,
  List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloudDownload from '@mui/icons-material/CloudDownload';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Description from '@mui/icons-material/Description';
import Clear from '@mui/icons-material/Clear';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  FaDownload, FaImages, FaFileArchive, FaClipboard, FaTrash,
} from 'react-icons/fa';
import InterfaceHeader from '../components/InterfaceHeader';
import RelatedToolsBar from '../components/RelatedToolsBar';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import { API_ENDPOINTS, apiCall } from '../config/api';
import { TOOL_COLORS } from '../constants/colors';
import { useProcess } from '../context/ProcessContext';
import { useActivityLog } from '../context/ActivityLogContext';
import { useDropzone } from 'react-dropzone';

// Reverse-direction tools — for users landing here without an Excel of
// links yet, point them at the two pages that generate one.
const AWS_LINK_GENERATORS = [
  {
    name:    'AWS File Links',
    path:    '/dashboard/aws-file-links',
    accent:  '#2563EB',
    Icon:    CloudUploadRoundedIcon,
    blurb:   "Don't have an Excel of links yet? Upload images here (or paste an Excel of Item IDs) and AWS File Links will push them to S3 and hand you back a spreadsheet of URLs.",
    ctaTag:  'Generate links',
  },
  {
    name:    'AWS File Links Alternate',
    path:    '/dashboard/aws-file-links-alternate',
    accent:  '#059669',
    Icon:    LayersRoundedIcon,
    blurb:   'Rugs- or furniture-specific link generator with shape filtering and size interchange. Useful when AWS File Links is too generic for your inventory.',
    ctaTag:  'Open rugs / furniture flow',
  },
];

const MAX_URLS = 500;

const BulkImageDownloader = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { addProcess, updateProcess, removeProcess } = useProcess();
  const { addActivityProcess, updateActivityProcess, completeActivityProcess, setLogs: setActivityLogs } = useActivityLog();


  const [urlText, setUrlText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [progress, setProgress] = useState(0);
  const [logs, setLogs] = useState([]);
  const [result, setResult] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const intervalRef = useRef(null);

  const urlCount = urlText.trim() ? urlText.trim().split('\n').filter(u => u.trim()).length : 0;

  // File drop for Excel — stage file, don't process immediately
  const onDrop = useCallback((files) => {
    if (!files.length) return;
    const file = files[0];
    if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
      setError('Please upload an Excel file (.xlsx, .xls) or CSV');
      return;
    }
    setExcelFile(file);
    setError(null);
    setSuccess(null);
  }, []);

  const handleProcessFile = async () => {
    if (!excelFile) return;
    setLoading(true); setError(null); setSuccess(null); setLogs([]); setResult(null);
    const activityId = `bulk-img-${Date.now()}`;
    const uiId = addProcess({ name: 'Bulk Image Download' });
    addActivityProcess(activityId, `Downloading images from ${excelFile.name}`, 'Bulk Image Downloader');
    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      const data = await apiCall(API_ENDPOINTS.BULK_IMAGE_DOWNLOADER_PROCESS, { method: 'POST', body: formData });
      if (data.processId) pollStatus(data.processId, uiId, activityId);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      updateProcess(uiId, { status: 'error' });
      completeActivityProcess(activityId, 'error', err.message);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'], 'application/vnd.ms-excel': ['.xls'], 'text/csv': ['.csv'] },
    multiple: false, noClick: loading,
  });

  const handleProcess = async () => {
    const urls = urlText.trim().split('\n').map(u => u.trim()).filter(u => u);
    if (!urls.length) { setError('Enter at least one URL'); return; }
    if (urls.length > MAX_URLS) { setError(`Maximum ${MAX_URLS} URLs. You entered ${urls.length}.`); return; }

    setLoading(true); setError(null); setSuccess(null); setLogs([]); setResult(null);
    const activityId = `bulk-img-${Date.now()}`;
    const uiId = addProcess({ name: 'Bulk Image Download' });
    addActivityProcess(activityId, `Downloading ${urls.length} images`, 'Bulk Image Downloader');

    try {
      const data = await apiCall(API_ENDPOINTS.BULK_IMAGE_DOWNLOADER_PROCESS_URLS, {
        method: 'POST', body: JSON.stringify({ urls }),
      });
      if (data.processId) pollStatus(data.processId, uiId, activityId);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      updateProcess(uiId, { status: 'error' });
      completeActivityProcess(activityId, 'error', err.message);
    }
  };

  const pollStatus = (serverProcessId, uiId, activityId) => {
    intervalRef.current = setInterval(async () => {
      try {
        const status = await apiCall(API_ENDPOINTS.BULK_IMAGE_DOWNLOADER_STATUS(serverProcessId));
        setProgress(status.progressPercentage || 0);
        setLogs(status.logs || []);
        updateActivityProcess(activityId, { progress: status.progressPercentage });
        if (status.logs && status.logs.length > 0) {
          setActivityLogs(activityId, status.logs);
        }

        if (status.status === 'completed') {
          clearInterval(intervalRef.current);
          setLoading(false);
          setResult({ processId: serverProcessId, fileName: status.fileName, processed: status.processedItems, failed: status.failedItems });
          setSuccess(`Downloaded ${status.processedItems} images${status.failedItems > 0 ? ` (${status.failedItems} failed)` : ''}`);
          updateProcess(uiId, { status: 'success' });
          setTimeout(() => removeProcess(uiId), 3000);
          completeActivityProcess(activityId, 'success', `${status.processedItems} images downloaded`);
          // Auto-download the ZIP
          try {
            const dlResp = await fetch(API_ENDPOINTS.BULK_IMAGE_DOWNLOADER_DOWNLOAD(serverProcessId), {
              headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` },
            });
            const blob = await dlResp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = status.fileName;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(url);
          } catch (dlErr) { console.warn('Auto-download failed:', dlErr); }
        } else if (status.status === 'error') {
          clearInterval(intervalRef.current);
          setLoading(false); setError('Processing failed');
          updateProcess(uiId, { status: 'error' });
          completeActivityProcess(activityId, 'error', 'Processing failed');
        }
      } catch { /* keep polling */ }
    }, 1000);
  };

  const handleDownloadTemplate = async () => {
    try {
      const resp = await fetch(`${API_ENDPOINTS.BULK_IMAGE_DOWNLOADER_PROCESS.replace('/process', '/sample')}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` },
      });
      if (!resp.ok) throw new Error('Failed to download template');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url;
      const now = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
      a.download = `Bulk Image Downloader Template ${now.replace(/,/g, '').replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { setError(err.message || 'Failed to download template'); }
  };

  const handleDownload = async () => {
    if (!result) return;
    try {
      const resp = await fetch(API_ENDPOINTS.BULK_IMAGE_DOWNLOADER_DOWNLOAD(result.processId), {
        headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` },
      });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = result.fileName;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSuccess('ZIP downloaded successfully');
    } catch (err) { setError('Download failed: ' + err.message); }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <InterfaceHeader title="Bulk Image Downloader" color={TOOL_COLORS.LINK.primary} category="link" />

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Upload & Process
              </Typography>

              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                Paste image URLs (one per line) or upload an Excel/CSV file with URLs in any column. All images will be downloaded and packaged into a ZIP file. Maximum {MAX_URLS} URLs per batch.
              </Typography>

              {/* Excel Upload Area */}
              <Card sx={{ mb: 3, bgcolor: 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: TOOL_COLORS.LINK.primary }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      Excel / CSV File
                    </Typography>
                    <Chip label="Option 1" size="small" variant="outlined" sx={{ fontSize: 11 }} />
                  </Box>

                  <Box {...getRootProps()} sx={{
                    border: `2px dashed ${isDragActive ? TOOL_COLORS.LINK.primary : excelFile ? '#4CAF50' : theme.palette.divider}`,
                    borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer', transition: 'all 0.3s ease', mb: 2,
                    bgcolor: isDragActive ? TOOL_COLORS.LINK.light : excelFile ? (isDark ? 'rgba(76,175,80,0.15)' : 'rgba(76,175,80,0.06)') : 'transparent',
                    '&:hover': { borderColor: excelFile ? '#4CAF50' : TOOL_COLORS.LINK.primary, bgcolor: excelFile ? (isDark ? 'rgba(76,175,80,0.2)' : 'rgba(76,175,80,0.1)') : TOOL_COLORS.LINK.light },
                  }}>
                    <input {...getInputProps()} />
                    {!excelFile ? (
                      <>
                        <CloudDownload sx={{ fontSize: 48, color: isDragActive ? TOOL_COLORS.LINK.primary : 'text.secondary', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>
                          {isDragActive ? 'Drop file here' : 'Drop your Excel or CSV file here or click to browse'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Supports .xlsx, .xls, .csv — URLs in any column will be extracted
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" gutterBottom>1 file selected</Typography>
                        <Typography variant="caption" color="text.secondary">Click or drop to replace</Typography>
                      </>
                    )}
                  </Box>

                  {/* Selected file info */}
                  {excelFile && (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      p: 1, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 1, mb: 2,
                      '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)', borderColor: TOOL_COLORS.LINK.primary },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Description sx={{ color: TOOL_COLORS.LINK.primary, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{excelFile.name}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>({(excelFile.size / 1024).toFixed(1)} KB)</Typography>
                      </Box>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExcelFile(null); }} sx={{ color: theme.palette.error.main }}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {excelFile && (
                      <Button variant="contained" onClick={handleProcessFile} disabled={loading}
                        startIcon={<FaDownload />}
                        sx={{ bgcolor: TOOL_COLORS.LINK.primary, '&:hover': { bgcolor: TOOL_COLORS.LINK.hover }, textTransform: 'none', fontWeight: 600 }}>
                        {loading ? 'Processing...' : 'Process File & Download as ZIP'}
                      </Button>
                    )}
                    <Button variant="outlined" onClick={handleDownloadTemplate} disabled={loading}
                      startIcon={<FaDownload />}
                      sx={{ borderColor: TOOL_COLORS.LINK.primary, color: TOOL_COLORS.LINK.primary, textTransform: 'none',
                        '&:hover': { borderColor: TOOL_COLORS.LINK.hover, bgcolor: `${TOOL_COLORS.LINK.primary}10` } }}>
                      Download Template
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              <Divider sx={{ my: 2 }}><Chip label="OR" size="small" /></Divider>

              {/* URL Input */}
              <Card sx={{ mb: 3, bgcolor: 'transparent' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <FaImages color={TOOL_COLORS.LINK.primary} size={18} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      Paste Image URLs
                    </Typography>
                    <Chip label="Option 2" size="small" variant="outlined" sx={{ fontSize: 11 }} />
                  </Box>

                  <TextField
                    fullWidth multiline rows={8} value={urlText} onChange={(e) => setUrlText(e.target.value)}
                    placeholder={"Paste image URLs here, one per line...\nhttps://example.com/image1.jpg\nhttps://example.com/image2.jpg"}
                    disabled={loading}
                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: 'monospace', fontSize: 12, bgcolor: isDark ? '#1e293b' : '#fff' } }}
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Chip label={`${urlCount} URL${urlCount !== 1 ? 's' : ''}`} size="small"
                      sx={{ fontWeight: 600, bgcolor: urlCount > MAX_URLS ? '#ef444420' : (isDark ? '#1e293b' : '#f1f5f9'), color: urlCount > MAX_URLS ? '#ef4444' : theme.palette.text.secondary }} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Paste from clipboard">
                        <IconButton size="small" onClick={async () => { const text = await navigator.clipboard.readText(); setUrlText(prev => prev ? prev + '\n' + text : text); }}
                          sx={{ color: theme.palette.text.secondary }}>
                          <FaClipboard size={14} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Clear all">
                        <IconButton size="small" onClick={() => { setUrlText(''); setResult(null); setLogs([]); setSuccess(null); }}
                          sx={{ color: theme.palette.text.secondary }}>
                          <FaTrash size={14} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button variant="contained" onClick={handleProcess} disabled={loading || urlCount === 0 || urlCount > MAX_URLS}
                  startIcon={<FaDownload />}
                  sx={{ bgcolor: TOOL_COLORS.LINK.primary, '&:hover': { bgcolor: TOOL_COLORS.LINK.hover }, px: 4, textTransform: 'none', fontWeight: 600 }}>
                  {loading ? 'Processing...' : `Download ${urlCount} Image${urlCount !== 1 ? 's' : ''} as ZIP`}
                </Button>

                {(urlText || result) && (
                  <Button variant="outlined" onClick={() => { setUrlText(''); setResult(null); setLogs([]); setError(null); setSuccess(null); }}
                    disabled={loading}
                    sx={{ borderColor: theme.palette.divider, color: theme.palette.text.primary, textTransform: 'none',
                      '&:hover': { borderColor: theme.palette.text.secondary } }}>
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Status & Downloads Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Downloads
              </Typography>

              {/* Progress */}
              {loading && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Downloading images...</Typography>
                  <LinearProgress variant="determinate" value={progress}
                    sx={{ height: 8, borderRadius: 4,
                      backgroundColor: isDark ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.1)',
                      '& .MuiLinearProgress-bar': { backgroundColor: TOOL_COLORS.LINK.primary, borderRadius: 4 } }} />
                  {progress > 0 && (
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5, display: 'block' }}>
                      {Math.round(progress)}% complete
                    </Typography>
                  )}
                </Box>
              )}

              {/* Error Alert */}
              {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

              {/* Success Alert */}
              {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>{success}</Alert>}

              {/* Results Summary */}
              {result && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Processing Results:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <FaImages color={TOOL_COLORS.LINK.primary} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${result.processed} Images Downloaded`}
                        secondary={result.failed > 0 ? `${result.failed} failed` : 'All images downloaded successfully'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <FaFileArchive color="#4CAF50" />
                      </ListItemIcon>
                      <ListItemText
                        primary="ZIP File Generated"
                        secondary={result.fileName}
                      />
                    </ListItem>
                  </List>
                  <Button fullWidth variant="contained" startIcon={<FaDownload />} onClick={handleDownload}
                    sx={{ mt: 1, textTransform: 'none', bgcolor: TOOL_COLORS.LINK.primary, '&:hover': { bgcolor: TOOL_COLORS.LINK.hover }, fontWeight: 600 }}>
                    Download ZIP
                  </Button>
                </Box>
              )}

              {!loading && !result && !error && !success && (
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                  Enter URLs or upload a file to start downloading images.
                </Typography>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Activity Log */}
              {logs.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Activity Log:
                  </Typography>
                  <Paper sx={{ p: 1.5, maxHeight: 200, overflowY: 'auto', borderRadius: 2, mb: 3,
                    bgcolor: isDark ? '#0f172a' : '#f8fafc', border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}` }}>
                    {logs.map((log, i) => (
                      <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: 10, color: theme.palette.text.secondary }}>{log}</Typography>
                    ))}
                  </Paper>
                  <Divider sx={{ my: 3 }} />
                </>
              )}

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                1. Paste image URLs (one per line) or upload Excel/CSV<br />
                2. Click "Download as ZIP" to process<br />
                3. Download the resulting ZIP file from this panel<br />
                4. Maximum {MAX_URLS} URLs per batch
              </Typography>
            </Paper>

            {/* "Next steps" — points users at the tools that generate
                the Excel of links they'd feed into this downloader. */}
            <RelatedToolsBar tools={AWS_LINK_GENERATORS} title="Need to generate links first?" subtitle="Generate an Excel of image links" layout="stack" />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default BulkImageDownloader;
